import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateBreadcrumbSchema } from '../../utils/seo';
import { calendarEvents } from '../../mocks/calendarEvents';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CalendarHero from './components/CalendarHero';
import CategoryFilter from './components/CategoryFilter';
import MonthCalendar from './components/MonthCalendar';
import EventList from './components/EventList';
import DateFilter from './components/DateFilter';
import FeaturedEventsBar from './components/FeaturedEventsBar';
import CalendarNewsletter from './components/CalendarNewsletter';

function getToday() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function getEndOfWeek() {
  const t = new Date();
  const dayOfWeek = t.getDay();
  const end = new Date(t);
  end.setDate(t.getDate() + (6 - dayOfWeek));
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

function getEndOfMonth() {
  const t = new Date();
  const end = new Date(t.getFullYear(), t.getMonth() + 1, 0);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

export default function CalendarPage() {
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activePreset, setActivePreset] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useSEO({
    title: 'Roseville Events Calendar 2025 | Community Festivals, Concerts & Activities | Roseville.org',
    description: 'Browse the complete Roseville community events calendar. Filter by category and date to find concerts, festivals, farmers markets, family events, and more happening in Roseville, CA.',
    keywords: 'Roseville events calendar, Roseville community events, Roseville festivals 2025, Roseville concerts, Placer County activities, things to do Roseville CA',
    canonical: '/calendar',
    ogImage: 'https://public.readdy.ai/ai/img_res/f7792263fad26dc49e310bfd2b6b3934.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: 'Roseville Community Events Calendar',
          description: 'Complete calendar of community events, festivals, concerts, and activities in Roseville, California.',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/calendar`,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: 'Roseville.org', url: import.meta.env.VITE_SITE_URL || 'https://roseville.org' },
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Events Calendar', url: '/calendar' },
        ]),
      ],
    },
  });

  const handlePreset = (preset: string) => {
    setActivePreset(preset);
    setSelectedDate(null);
    if (preset === 'today') {
      const today = getToday();
      setStartDate(today);
      setEndDate(today);
    } else if (preset === 'week') {
      setStartDate(getToday());
      setEndDate(getEndOfWeek());
    } else if (preset === 'month') {
      setStartDate(getToday());
      setEndDate(getEndOfMonth());
    } else {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleDateSelect = (date: string) => {
    if (selectedDate === date) {
      setSelectedDate(null);
      setActivePreset('all');
      setStartDate('');
      setEndDate('');
    } else {
      setSelectedDate(date);
      setStartDate(date);
      setEndDate(date);
      setActivePreset('');
    }
  };

  const handleStartChange = (val: string) => {
    setStartDate(val);
    setActivePreset('');
    setSelectedDate(null);
  };

  const handleEndChange = (val: string) => {
    setEndDate(val);
    setActivePreset('');
    setSelectedDate(null);
  };

  const filteredEvents = useMemo(() => {
    let result = [...calendarEvents];

    if (selectedCategory !== 'all') {
      result = result.filter((e) => e.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.location.toLowerCase().includes(q) ||
          e.description.toLowerCase().includes(q)
      );
    }

    if (startDate) {
      result = result.filter((e) => e.date >= startDate);
    }
    if (endDate) {
      result = result.filter((e) => e.date <= endDate);
    }

    return result.sort((a, b) => a.date.localeCompare(b.date));
  }, [selectedCategory, startDate, endDate, searchQuery]);

  const totalCount = filteredEvents.length;

  return (
    <div className="min-h-screen bg-gray-50/50">
      <Navbar />
      <CalendarHero />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Top controls */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search events..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent"
            />
          </div>

          {/* View toggle & count */}
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">
              <strong className="text-gray-900">{totalCount}</strong> event{totalCount !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'calendar'
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                <span>Calendar</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'list'
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className="ri-list-unordered w-4 h-4 flex items-center justify-center"></i>
                <span>List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Category filter */}
        <div className="mb-8">
          <CategoryFilter selected={selectedCategory} onSelect={setSelectedCategory} />
        </div>

        {/* Main content */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left: Main view */}
          <div className="flex-1 min-w-0">
            {viewMode === 'calendar' ? (
              <>
                <MonthCalendar
                  events={filteredEvents}
                  currentMonth={currentMonth}
                  onMonthChange={setCurrentMonth}
                  onDateSelect={handleDateSelect}
                  selectedDate={selectedDate}
                />
                {/* Events for selected date */}
                {selectedDate && (
                  <div className="mt-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2">
                      <i className="ri-calendar-check-line w-5 h-5 flex items-center justify-center text-teal-600"></i>
                      <span>
                        Events on{' '}
                        {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </h3>
                    <EventList
                      events={filteredEvents.filter((e) => e.date === selectedDate)}
                    />
                  </div>
                )}
              </>
            ) : (
              <EventList events={filteredEvents} />
            )}

            {/* Featured events */}
            <FeaturedEventsBar events={filteredEvents} />
          </div>

          {/* Right sidebar */}
          <div className="w-full lg:w-72 flex-shrink-0 space-y-6">
            <DateFilter
              startDate={startDate}
              endDate={endDate}
              onStartChange={handleStartChange}
              onEndChange={handleEndChange}
              onPreset={handlePreset}
              activePreset={activePreset}
            />

            {/* Quick links */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Links</h3>
              <div className="space-y-2">
                <Link
                  to="/events"
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer py-1.5"
                >
                  <i className="ri-calendar-event-line w-4 h-4 flex items-center justify-center text-teal-500"></i>
                  <span>All Events</span>
                </Link>
                <Link
                  to="/recreation"
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer py-1.5"
                >
                  <i className="ri-run-line w-4 h-4 flex items-center justify-center text-teal-500"></i>
                  <span>Recreation</span>
                </Link>
                <Link
                  to="/food-drink"
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer py-1.5"
                >
                  <i className="ri-restaurant-line w-4 h-4 flex items-center justify-center text-teal-500"></i>
                  <span>Food &amp; Drink</span>
                </Link>
                <Link
                  to="/directory"
                  className="flex items-center space-x-2 text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer py-1.5"
                >
                  <i className="ri-building-line w-4 h-4 flex items-center justify-center text-teal-500"></i>
                  <span>Business Directory</span>
                </Link>
              </div>
            </div>

            {/* Newsletter */}
            <CalendarNewsletter formUrl="https://readdy.ai/api/form/d6alk7ua728k8ctu3csg" />

            {/* Submit event CTA */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 p-5 text-center">
              <i className="ri-add-circle-line text-2xl text-amber-600 mb-2 block"></i>
              <h3 className="text-sm font-bold text-gray-900 mb-1">Have an Event?</h3>
              <p className="text-xs text-gray-600 mb-3">
                Submit your community event to be featured on our calendar.
              </p>
              <Link
                to="/submit-business"
                className="inline-block bg-amber-500 text-white px-5 py-2 rounded-lg text-xs font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Submit Event
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Source Attribution */}
      <section className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-100 pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-database-2-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Calendar event data sourced from <strong className="text-gray-700">City of Roseville Parks &amp; Recreation</strong>, <strong className="text-gray-700">Downtown Roseville Partnership</strong>, <strong className="text-gray-700">Roseville Arts Council</strong>, and verified community submissions.
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    Data last updated: March 22, 2025
                  </p>
                </div>
              </div>
              <a href="/data-sources" className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer transition-colors flex-shrink-0">
                View All Sources <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
